import clamp from "lodash/clamp";
import { RefObject, SyntheticEvent, useRef } from "react";

type UseStickyControlsParams = {
  headerRef: RefObject<HTMLDivElement | null>;
  scrollableContainerRef: RefObject<HTMLDivElement | null>;
  filtersRef: RefObject<HTMLDivElement | null>;
  shadowRef: RefObject<HTMLDivElement | null>;
  onFiltersVisibilityChange: (visible: boolean) => void;
};

export const useStickyControls = ({
  headerRef,
  scrollableContainerRef,
  filtersRef,
  shadowRef,
  onFiltersVisibilityChange
}: UseStickyControlsParams) => {
  const lastScrollY = useRef(0);
  const currentTranslateY = useRef(0);
  const disableScrollHandler = useRef(false);

  const recalculateShadow = () => {
    if (!shadowRef.current || !scrollableContainerRef.current) {
      return;
    }
    const scrollTop = scrollableContainerRef.current?.scrollTop || 0;
    if (scrollTop > Math.abs(currentTranslateY.current)) {
      shadowRef.current.style.opacity = "1";
    } else {
      shadowRef.current.style.opacity = "0";
    }
  };

  const handleScroll = (event: SyntheticEvent) => {
    if (disableScrollHandler.current) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = event.target as HTMLDivElement;

    // Prevent from Safari elastic scrolling
    const maxScroll = scrollHeight - clientHeight;
    const normalizedScroll = clamp(scrollTop, 0, maxScroll);

    const filtersHeight = filtersRef.current?.offsetHeight || 0;
    const delta = normalizedScroll - lastScrollY.current;
    const nextTranslateY = clamp(currentTranslateY.current - delta, -filtersHeight, 0);

    const filtersCurrentlyVisible = Math.abs(currentTranslateY.current) < filtersHeight;
    const filtersNextlyVisible = Math.abs(nextTranslateY) < filtersHeight;
    const filtersBecameFullyHidden = filtersCurrentlyVisible && !filtersNextlyVisible;
    const filtersBecameVisible = !filtersCurrentlyVisible && filtersNextlyVisible;

    if (filtersBecameFullyHidden) {
      onFiltersVisibilityChange(false);
    } else if (filtersBecameVisible) {
      onFiltersVisibilityChange(true);
    }

    if (headerRef.current) {
      headerRef.current.style.transform = `translateY(${nextTranslateY}px)`;
      currentTranslateY.current = nextTranslateY;
    }

    lastScrollY.current = normalizedScroll;
    recalculateShadow();
  };

  const handleAccordionStateChange = () => {
    // Avoid controls panel height change changing the scroll position
    // and thus triggering the scroll handler, which results in a "transform" style change
    disableScrollHandler.current = true;
    setTimeout(() => {
      disableScrollHandler.current = false;
      lastScrollY.current = scrollableContainerRef.current?.scrollTop || 0;
      recalculateShadow();
      // timeout > accordion animation
    }, 250);
  };

  return { handleScroll, handleAccordionStateChange };
};
